import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const BackendVisualizationPanel = ({ 
  svgSrc,
  lines = [],
  side = 'left', // 'left' or 'right'
  maxWidth = '39%',
  strokeColor = '#00ffff',
  strokeWidth = 3.5,
  dashLength = 8,
  gapLength = 5,
  speed = 75,
  opacity = 0.8,
  showGrid = false,
  showPoints = false,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const offsetRef = useRef(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d', { alpha: true });

    // Set canvas size to match SVG's actual rendered size
    const resizeCanvas = () => {
      const svgImg = containerRef.current?.querySelector('img');
      if (svgImg) {
        const rect = svgImg.getBoundingClientRect();
        const width = Math.round(rect.width);
        const height = Math.round(rect.height);
        
        canvas.width = width;
        canvas.height = height;
        
        // Set CSS dimensions to match canvas attributes (prevents stretching)
        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';
      }
    };

    resizeCanvas();

    // Watch SVG element for size changes (not the container)
    let resizeObserver = null;
    const svgImg = containerRef.current?.querySelector('img');
    if (svgImg && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas();
      });
      resizeObserver.observe(svgImg);
    }

    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('orientationchange', resizeCanvas);

    let lastTime = Date.now();

    const drawDashedLine = (fromX, fromY, toX, toY, offset) => {
      const dx = toX - fromX;
      const dy = toY - fromY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);

      ctx.save();
      ctx.translate(fromX, fromY);
      ctx.rotate(angle);

      const dashPattern = dashLength + gapLength;
      let position = -offset % dashPattern;

      while (position < distance) {
        const dashStart = Math.max(position, 0);
        const dashEnd = Math.min(position + dashLength, distance);

        if (dashEnd > dashStart) {
          ctx.beginPath();
          ctx.moveTo(dashStart, 0);
          ctx.lineTo(dashEnd, 0);
          ctx.stroke();
        }

        position += dashPattern;
      }

      ctx.restore();
    };

    const drawGrid = () => {
      const gridSize = 50; // pixels
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.3;

      // Vertical lines
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }

      // Horizontal lines
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Draw 0.1 increment markers (10% lines)
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.1)';
      ctx.lineWidth = 0.5;
      
      const smallGridSize = canvas.width / 10;
      for (let x = 0; x < canvas.width; x += smallGridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
    };

    const drawPoints = () => {
      const pointRadius = 4;
      const fontSize = 10;
      
      ctx.font = `${fontSize}px monospace`;
      ctx.fillStyle = 'rgba(255, 100, 100, 0.8)';
      ctx.strokeStyle = 'rgba(255, 100, 100, 1)';
      ctx.lineWidth = 1;
      ctx.globalAlpha = 1;

      lines.forEach(line => {
        if (line.points && line.points.length > 0) {
          line.points.forEach(point => {
            const x = point.x * canvas.width;
            const y = point.y * canvas.height;

            // Draw point circle
            ctx.beginPath();
            ctx.arc(x, y, pointRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            // Draw point coordinates label
            const label = `(${point.x.toFixed(2)}, ${point.y.toFixed(2)})`;
            ctx.fillStyle = 'rgba(255, 100, 100, 0.9)';
            ctx.fillText(label, x + pointRadius + 5, y - pointRadius - 2);
          });
        }
      });
    };

    const animate = () => {
      const currentTime = Date.now();
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      offsetRef.current += speed * deltaTime;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw grid if enabled
      if (showGrid) {
        ctx.globalAlpha = 1;
        drawGrid();
      }

      // Set stroke style
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.globalAlpha = opacity;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw all lines
      lines.forEach(line => {
        if (line.points && line.points.length > 1) {
          for (let i = 0; i < line.points.length - 1; i++) {
            const p1 = line.points[i];
            const p2 = line.points[i + 1];
            
            const x1 = p1.x * canvas.width;
            const y1 = p1.y * canvas.height;
            const x2 = p2.x * canvas.width;
            const y2 = p2.y * canvas.height;
            
            drawDashedLine(x1, y1, x2, y2, offsetRef.current);
          }
        }
      });

      // Draw points if enabled
      if (showPoints) {
        drawPoints();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('orientationchange', resizeCanvas);
    };
  }, [lines, strokeColor, strokeWidth, speed, opacity, dashLength, gapLength, showGrid, showPoints]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        maxWidth: maxWidth || '500px', // Fallback to 500px if no maxWidth prop
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* SVG Image */}
      <Box
        component="img"
        src={svgSrc}
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          pointerEvents: 'none',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Canvas Overlay */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
    </Box>
  );
};

export default BackendVisualizationPanel;
